import React from "react";
import { ReactComponent as DriverIcon } from "../../../assets/icons/two-user.svg";
import DashboardCard from "../../../components/ui/dashboard/DashboardCard";
import StatsLoading from "../../../components/ui/dashboard/StatsLoading";
import { statsIconConfig } from "../../../lib/constant";
import { useGetCompanyDriverStatsQuery } from "../driverApiSlice";

export interface VehicleTypeStat {
  vehicleType: string;
  driverCount: number;
  percentage: number;
}

export type DriverStatsResponse = {
  data: {
    vehicleTypeStats: VehicleTypeStat[];
    totalDrivers: number;
  };
};

// Driver icon mapping configuration by vehicle type

const DashboardDriverStats: React.FC = () => {
  const { data, isLoading, isSuccess, isError, error } =
    useGetCompanyDriverStatsQuery();

  // Handle loading state with skeleton
  if (isLoading) {
    return <StatsLoading />;
  }

  // Handle error state
  if (isError) {
    return (
      <div className="flex flex-wrap gap-x-4 gap-y-4 sm:gap-x-10 sm:gap-y-6 bg-white rounded-[10px] p-4">
        <div className="text-red-500">Error loading driver statistics</div>
      </div>
    );
  }

  // Render separator component
  const Separator = () => <div className="hidden sm:block bg-[#E2E2E2] h-[54px] w-[2px]"></div>;

  // Custom DashboardCard component for drivers that shows percentage
  const DriverDashboardCard: React.FC<{
    icon: React.ReactNode;
    iconClassName: string;
    value: number;
    label: string;
    percentage: number;
  }> = ({ icon, iconClassName, value, label, percentage }) => (
    <DashboardCard
      icon={icon}
      iconClassName={iconClassName}
      value={value}
      label={`${label} (${percentage.toFixed(1)}%)`}
    />
  );

  return (
    <div className="flex flex-wrap gap-x-4 gap-y-4 sm:gap-x-10 sm:gap-y-6 bg-white rounded-[10px] p-4">
      {/* Total Drivers Card */}
      <DashboardCard
        icon={<DriverIcon className="h-[31.7px] w-[31.7px]" />}
        iconClassName="bg-[#E7F2F5]"
        value={data?.data?.totalDrivers || 0}
        label="Total drivers"
      />

      <Separator />

      {/* Dynamic Driver Type Cards */}
      {data?.data?.vehicleTypeStats?.map((driverStat, index) => {
        const vehicleTypeKey =
          driverStat.vehicleType.toLowerCase() as keyof typeof statsIconConfig;
        const config = statsIconConfig[vehicleTypeKey];

        // Fallback config for unknown vehicle types
        const fallbackConfig = {
          icon: <DriverIcon className="h-[31.7px] w-[31.7px]" />,
          iconClassName: "bg-[#F5F5F5]",
          label: `${driverStat.vehicleType} Drivers`,
        };

        const currentConfig = config || fallbackConfig;

        return (
          <React.Fragment key={driverStat.vehicleType}>
            <DriverDashboardCard
              icon={currentConfig.icon}
              iconClassName={currentConfig.iconClassName}
              value={driverStat.driverCount}
              label={currentConfig.label}
              percentage={driverStat.percentage}
            />
            {/* Add separator between cards except for the last one */}
            {index < (data?.data?.vehicleTypeStats?.length || 0) - 1 && (
              <Separator />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default DashboardDriverStats;
